<template>
  <vx-card class="mt-base" title="Law Activity 7: Civil Case Scenario - Part #1">
    <h4 class="mb-5">Background to the Case: The Plaintiff’s Claim</h4>
    <p class="mb-5">
      A security guard (the plaintiff) was injured at work when she was punched by a patron outside
      a party. She injured her neck and back and needs surgery. She does not recover from her
      injuries and becomes depressed. The plaintiff sues her employer (the security company) and the
      venue operator for negligence, alleging they both owed her a duty of care and breached this
      duty by failing to train her properly and failing to carry out a proper risk assessment.
    </p>
    <p class="mb-5">
      Both defendants deny they were negligent and argue in defence that there was contributory
      negligence by the plaintiff and a voluntary assumption of risk. The plaintiff had followed the
      patron outside onto the street in contravention of her employer’s training manual. The
      plaintiff elects to have the matter heard by a jury, which finds that both defendants were
      negligent and did not provide the security guard with a safe working environment and adequate
      support. The plaintiff’s lawyer argues she should receive general damages for pain and
      suffering and special damages for lost earnings.

      <centered-image-wrapper
        :image="videos[1].image"
        classes="2/3"
        label="Play video: Civil Trial, Episode 1"
        @clicked="showIframePopup(videos[1].video)"
      />
    </p>

    <p class="mb-5">
      In this video, you will have heard references made to several phrases and terms that might be
      unfamiliar to you. Here’s a chance to hone those research skills that can be crucial for so
      many jobs in the legal field.
    </p>
    <h4 class="mb-5">Your Task:</h4>
    <p class="mb-5">
      Look at the table below. In the left column, you’ll see a list of terms used in the video when
      discussing this civil case. Your task is to write a brief explanation of what each term means.
    </p>
    <p class="mb-5">
      You can use the information provided in the video, but you can also do a little internet
      research to flesh out these explanations where necessary. You can even cut and paste the
      definitions in from a suitable website - but be careful, make sure you take the time to
      understand the terms. Sometimes it can be helpful to rewrite the definitions you find into
      your own words.
    </p>

    <table class="table default-table">
      <tr class="header">
        <th width="50%">TERM</th>
        <th>EXPLANATION</th>
      </tr>
      <tr class="body" v-for="(term, index) in terms" :key="index">
        <td>{{ term.title }}</td>
        <td><text-block v-model="data['question' + index]" :editable="isEditable" /></td>
      </tr>
    </table>
    <save-buttons :is-editable="isEditable" @save="save" />
    <p class="my-5">
      If you would like to see some sample answers you can click the button below….. Beware …… your
      teacher will be able to see if you just copied these answers.
    </p>
    <button-link-wrapper @clicked="popupActive = !popupActive" text="See Sample Answers" />

    <vs-popup title="Sample Answers" :active.sync="popupActive">
      <table class="table default-table">
        <tr class="header">
          <th width="50%">TERM</th>
          <th>EXPLANATION</th>
        </tr>
        <tr class="body" v-for="(term, index) in terms" :key="index">
          <td>{{ term.title }}</td>
          <td>{{ term.explanation }}</td>
        </tr>
      </table>
    </vs-popup>
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import methods from '@/views/WorkExperiences/components/activity_js/methods';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/law/civil_case_part_1_splash.jpg'),
          video: 'https://www.youtube.com/embed/0wVePcBh9lM',
        },
      },
      terms: [
        {
          title: 'Duty of Care',
          explanation:
            'A duty of care is a legal obligation imposed on a person to take a reasonable standard of care when doing acts that could foreseeably harm others.',
        },
        {
          title: 'Breach of Duty',
          explanation:
            'A defendant breaches a duty of care by failing to exercise reasonable care in fulfilling the duty.',
        },
        {
          title: 'Negligence',
          explanation:
            'Negligence is a failure to take reasonable care to avoid causing injury or loss to another person.',
        },
        {
          title: 'Contributory Negligence',
          explanation:
            'Contributory negligence is failure by a person (typically the plaintiff) to take reasonable care for their own safety, which contributes to the harm the person suffers.',
        },
        {
          title: 'Voluntary Assumption of Risk',
          explanation:
            'A duty of care does not exist in circumstances where a person has voluntarily assumed a risk which then materialises.',
        },
        {
          title: 'Balance of Probabilities',
          explanation:
            'The court must decide whether it is satisfied to the requisite degree that the matter alleged has been proven. In civil cases, the decision-maker must be satisfied that the allegation has been proven on the balance of probabilities, while criminal matters require that the court be satisfied beyond a reasonable doubt.',
        },
        {
          title: 'Causation',
          explanation: `Causation, in legal terms, refers to the relationship of cause and effect between one event or action and the result. It is the act or process that produces an effect.<br/><br/>

In a personal injury case, one must establish causation—meaning that it’s not enough to show that the defendant was negligent. The negligence must be what caused the complainant’s injuries.`,
        },
        {
          title: 'Apportionment of Liability',
          explanation:
            "After determining an award of damages in an action, a court is to apportion the total amount of the damages between all persons who are found in that action to be jointly and severally liable for those damages, having regard to the extent of each person's responsibility for the damage.",
        },
        {
          title: 'Pecuniary Loss Damages',
          explanation:
            'Damages in a personal injury case that we can quantify in financial terms, e.g., lost earning in the past and potentially into the future.',
        },
        {
          title: 'Pain and Suffering Damages',
          explanation: 'Compensation for the loss of enjoyment of life.',
        },
      ],
      popupActive: false,
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>
